import { Injectable } from '@angular/core';
import { combineLatest, Observable, Subject, throwError } from 'rxjs';
import { LoggingService } from './logging.service';
import { ChatRule } from '../models/chat-rule.model';
import { LogEvent } from '../models/log-event.model';
import { ConditionFailEvent } from '../models/condition-fail-event.model';
import { v4 as uuid } from 'uuid';
import { catchError, tap } from 'rxjs/operators';
import { SkillService } from './skill.service';

export class ChatContext {
    sessionId: string;
    location: Location;
    title: string;
    chatRuleData: ChatRule;
}

@Injectable()
export class NotificationService {
    public location$ = new Subject<ChatContext>();
    public chatState$ = new Subject<boolean>();
    public proactiveChatError$ = new Subject<string>();
    skillAvailabilityCheckTimer: any;
    constructor(
        private loggingService: LoggingService,
        private skillService: SkillService
    ) {
        window.addEventListener("message", (ev) => this.receiveMessage(ev));
        this.ready();
    }

    receiveMessage(event: MessageEvent) {
        // TODO: Check the origin of event to prevent cross-site scripting attacks.
        console.info('[ProactiveChat] Received %c%s', 'color:magenta', event.data.message, { data: event.data, origin: event.origin });

        switch (event.data.message) {
            case 'uwfStartProactive':
                this.requestLocation();
                break;
            case 'uwfChatStateChanged':
                this.chatState$.next(event.data.isChatOpen);
                break;
            case 'uwfLocationChanged':
                const chatContext: ChatContext = {
                    sessionId: uuid(),
                    location: event.data.location,
                    title: event.data.title,
                    chatRuleData: event.data.chatRuleData
                };
                this.location$.next(chatContext);
                break;
            case 'uwfProactiveError':
                this.proactiveChatError$.next(event.data.error);
                break;
            case 'uwfCreateProactiveChatLog':
                this.loggingService.logEvent(new LogEvent(event.data.eventCode, event.data.eventDescription));
                break;
            case 'uwfStartPeriodicSkillAvailabilityCheck':
                this.onStartSkillAvailabilityCheck(event.data);
                break;
            case 'uwfStopPeriodicSkillAvailabilityCheck':
                this.onStopSkillAvailabilityCheck();
                break;
            default:
                console.warn('[ProactiveChat] Unknown message received:', event.data.message);
        }
    }

    sendConditionFailEvent(error: ConditionFailEvent) {
        parent.postMessage({
            message: 'uwfProactiveConditionError',
            errorInfo: error
        }, '*');
    }

    sendApplicationStarted(sessionId: string) {
        parent.postMessage({
            message: 'uwfProactiveEngineLoaded',
            sessionId: sessionId
        }, '*');
    }

    requestLocation() {
        parent.postMessage('uwfGetLocation', '*');
    }

    ready() {
        parent.postMessage('uwfChatEngineReady', '*');
    }

    onStartSkillAvailabilityCheck(options: any) {
        const skillAddressesFromPreChatForm = options.data.skillAddressesFromPreChatForm;
        const overriddenSkillAddresses = options.data.overriddenSkillAddresses;
        const isFormExternal = options.data.isFormExternal;
        const channelSubType = options.data.channelSubType;

        let defaultSkillAddress$: Observable<string> = this.skillService.getDefaultSkillAddress(channelSubType).pipe(
            catchError(err => {
                console.error('Failed to get default skill address');
                return throwError(err);
            }),
            tap(() => this.logWithTimestamp('Init', 'Successfully fetched default skill address'))
        );

        let skillAvailabilityCheckSeconds$: Observable<number> = this.skillService.getSkillAvailabilityCheckSeconds().pipe(
            catchError(err => {
                console.error('Failed to get skill availability check seconds');
                return throwError(err);
            }),
            tap(() => this.logWithTimestamp('Init', 'Successfully fetched skill availability check seconds'))
        );

        combineLatest(defaultSkillAddress$, skillAvailabilityCheckSeconds$).subscribe({
            next: data => {
                let [defaultSkillAddress, skillAvailabilityCheckSeconds] = data;
                let skillAddresses: string[];
                if (overriddenSkillAddresses) {
                    // Override skill addresses used for availability check
                    skillAddresses = overriddenSkillAddresses.split(',');
                } else if (overriddenSkillAddresses === '') {
                    // Skip availability check (with empty array)
                    skillAddresses = [];
                } else if (!isFormExternal) {
                    skillAddresses = skillAddressesFromPreChatForm.length ? skillAddressesFromPreChatForm : [defaultSkillAddress];
                }else { // Custom form is being used and there is no overriddenSkillAddresses defined
                    skillAddresses = [defaultSkillAddress];
                }

                this.logWithTimestamp('Init', 'Check skill address for ' + JSON.stringify(skillAddresses));
                this.checkSkillAvailability(skillAddresses);
                console.debug('Init: SkillAvailabilityCheckSeconds:', skillAvailabilityCheckSeconds);

                if (skillAvailabilityCheckSeconds > 0) {
                    clearInterval(this.skillAvailabilityCheckTimer);
                    this.skillAvailabilityCheckTimer = setInterval(() => {
                        this.checkSkillAvailability(skillAddresses);
                    }, skillAvailabilityCheckSeconds * 1000);
                }
            },
            error: err => {
                console.error(err);
            }
        });
    }

    onStopSkillAvailabilityCheck() {
        clearInterval(this.skillAvailabilityCheckTimer);
    }

    checkSkillAvailability(skillAddresses: string[]): void {
        if (skillAddresses.length === 0) {
            this.skillService.makeSkillAvailable();
            return;
        }

        this.skillService.getSkillStatus(skillAddresses).subscribe({
            next: data => {
                let skillAvailabilityList = data;
                if (skillAvailabilityList.some(skillAvailability => skillAvailability.Status === 'open')) {
                    this.skillService.makeSkillAvailable(); // Enable chat if any skills are open
                } else {
                    this.skillService.hideChatButton();
                }
            }
        });
    }

    logWithTimestamp(tag: string, msg: string) {
        console.debug(tag + ': ' + msg + ' at ' + new Date().toLocaleTimeString());
    }
}
